import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { Home, Calendar, ListChecks, AlertTriangle, MoreHorizontal } from "lucide-react";
import Shell from "../../components/Shell";
import EmployeeDashboard from "./EmployeeDashboard";
import EmployeeCalendar from "./EmployeeCalendar";
import EmployeeHistory from "./EmployeeHistory";
import EmployeeProfile from "./EmployeeProfile";
import EmployeeIssues from "./EmployeeIssues";
import EmployeeExtras from "./EmployeeExtras";
import EmployeeReportWizard from "./EmployeeReportWizard";
import SupervisionReportsList from "../reports/SupervisionReportsList";
import SupervisionReportDetail from "../reports/SupervisionReportDetail";
import useGalleryEnabled from "../../lib/useGalleryEnabled";

// Iter182 (v182): the legacy checklist (kitchen / bathroom / bedroom
// 13-item list) inside the per-task page is GONE. The Supervision
// Report flow is now the only employee workflow. Any leftover deep
// link to /funcionario/tarefa/:id auto-redirects to the new wizard
// for that booking, so the employee never sees the old checklist UI
// (and it cannot accidentally come back via a stale link in an email
// or notification).
function TaskRedirect() {
  const { id } = useParams();
  return <Navigate to={`/funcionario/relatorios/novo/${id}`} replace />;
}

// Iter167 (v167): the legacy "Checklist" + "Fotos" + "Histórico" tabs were
// superseded by the new Supervision Reports flow (single multi-section
// wizard with photos + checklist + signatures unified). The old routes are
// kept reachable via deep links for backwards-compat with existing emails
// and notifications, but they are no longer surfaced in the menu.
const BASE_NAV = [
  { key: "dashboard", path: "/funcionario", label: "Dashboard" },
  { key: "calendar", path: "/funcionario/calendario", label: "Calendário" },
  { key: "reports", path: "/funcionario/relatorios", label: "Relatórios" },
  { key: "issues", path: "/funcionario/problemas", label: "Problemas" },
  { key: "extras", path: "/funcionario/extras", label: "Extras" },
  { key: "history", path: "/funcionario/historico", label: "Histórico" },
  { key: "profile", path: "/funcionario/perfil", label: "Perfil" },
];

/** Mobile bottom nav: the 5 most-used destinations. "Mais" opens the drawer
 *  with the remaining entries (Extras, Histórico, Perfil). */
const BOTTOM_NAV = [
  { key: "dashboard", path: "/funcionario",             label: "Hoje",       icon: Home },
  { key: "calendar",  path: "/funcionario/calendario", label: "Agenda",     icon: Calendar },
  { key: "reports",   path: "/funcionario/relatorios", label: "Relatórios", icon: ListChecks },
  { key: "issues",    path: "/funcionario/problemas",  label: "Problemas",  icon: AlertTriangle },
  { key: "more",      label: "Mais",        icon: MoreHorizontal },
];

export default function EmployeeApp() {
  const galleryEnabled = useGalleryEnabled();
  const NAV = galleryEnabled
    ? [...BASE_NAV, { key: "gallery", path: "/galeria", label: "Galeria" }]
    : BASE_NAV;
  return (
    <Shell title="Área Funcionário" nav={NAV} testIdPrefix="emp" bottomNavItems={BOTTOM_NAV}>
      <Routes>
        <Route index element={<EmployeeDashboard />} />
        <Route path="tarefa/:id" element={<TaskRedirect />} />
        <Route path="calendario" element={<EmployeeCalendar />} />
        {/* Iter171: legacy checklist + photos routes removed — the new
            Supervision Report flow consolidates both. Old deep-links to
            /funcionario/checklist or /funcionario/fotos now redirect to
            the reports list via the catch-all <Navigate /> below. */}
        <Route path="relatorios" element={<SupervisionReportsList basePath="/funcionario/relatorios" />} />
        <Route path="relatorios/novo/:bookingId" element={<EmployeeReportWizard />} />
        <Route path="relatorios/:id" element={<EmployeeReportWizard />} />
        <Route path="relatorios/:id/ver" element={<SupervisionReportDetail basePath="/funcionario/relatorios" />} />
        <Route path="checklist" element={<Navigate to="/funcionario/relatorios" replace />} />
        <Route path="fotos" element={<Navigate to="/funcionario/relatorios" replace />} />
        <Route path="problemas" element={<EmployeeIssues />} />
        <Route path="extras" element={<EmployeeExtras />} />
        <Route path="historico" element={<EmployeeHistory />} />
        <Route path="perfil" element={<EmployeeProfile />} />
        <Route path="*" element={<Navigate to="/funcionario" replace />} />
      </Routes>
    </Shell>
  );
}
